import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Radio, Wifi, WifiOff, PlaneTakeoff, Clock,
  Pause, Play, XCircle, ChevronRight, Globe, Monitor,
  RefreshCw, AlertTriangle, Search, Filter, User, UserCheck, Hourglass, Timer
} from 'lucide-react';
import toast from 'react-hot-toast';
import { gateSessionsAPI, flightSyncAPI, airportAnnouncementsAPI } from '../../services/api';
import { useSupervisorSocket } from '../../hooks/useGateSocket';
import { gateSessionStatusKey } from '../../utils/gateSessionStatus';
import EmptyState from '../../components/UI/EmptyState';
import Button from '../../components/UI/Button';
import { useAuth } from '../../context/AuthContext';
import { queuePathFor } from '../../utils/queuePath';
import { languagePairLabel } from '../../utils/languageLabel';
import { sessionProgress } from '../../utils/sessionProgress';

// One visual theme per session state — the whole card takes the state's color so
// a supervisor/manager can read the floor at a glance:
//   grey = awaiting Acknowledge · orange = acknowledged (waiting for Initiate)
//   green = initiated / live · red = emergency-paused
// Acknowledged is orange from the moment it's acknowledged (both of
// gateSessionStatusKey's time-based tiers map here); "departing soon" is shown
// separately on the card's countdown.
const THEMES = {
  pending: {
    label: 'Awaiting acknowledge', icon: Hourglass,
    card: 'bg-white border-slate-200', band: 'bg-slate-100 text-slate-800', pill: 'bg-white text-slate-600 ring-1 ring-slate-200',
    sub: 'text-slate-500',
  },
  acknowledged: {
    label: 'Acknowledged', icon: UserCheck,
    card: 'bg-orange-50/70 border-orange-300', band: 'bg-gradient-to-r from-orange-500 to-amber-500 text-white', pill: 'bg-white/25 text-white',
    sub: 'text-orange-100',
  },
  active: {
    label: 'Live', icon: Radio, live: true,
    card: 'bg-emerald-50/70 border-emerald-300', band: 'bg-gradient-to-r from-emerald-600 to-teal-500 text-white', pill: 'bg-white/25 text-white',
    sub: 'text-emerald-100',
  },
  paused: {
    label: 'Paused', icon: Pause,
    card: 'bg-rose-50/70 border-rose-300', band: 'bg-gradient-to-r from-rose-600 to-red-500 text-white', pill: 'bg-white/25 text-white',
    sub: 'text-rose-100',
  },
};
const themeKey = (session) => {
  const k = gateSessionStatusKey(session);
  return k.startsWith('acknowledged') ? 'acknowledged' : (THEMES[k] ? k : 'pending');
};

const TIMELINE_HOURS = 4;

// Infinite scroll: cards render in pages as the supervisor scrolls down, for both
// the Next-4h and All-Time views. Sessions are already loaded in one request, so
// this keeps a long All-Time list fast to render without extra API calls.
const PAGE_SIZE = 12;

const FILTERS = [
  { key: 'all',          label: 'All' },
  { key: 'pending',      label: 'Awaiting' },
  { key: 'acknowledged', label: 'Acknowledged' },
  { key: 'active',       label: 'Live' },
  { key: 'paused',       label: 'Paused' },
];

function departureCountdown(iso) {
  const mins = Math.round((new Date(iso) - Date.now()) / 60000);
  if (mins <= 0) return { text: 'Departed', urgent: true };
  const h = Math.floor(mins / 60), m = mins % 60;
  return { text: h ? `in ${h}h ${m}m` : `in ${m}m`, urgent: mins <= 15 };
}

export default function SupervisorDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [gateStatuses, setGateStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // FILTERS key
  // CR 9.2 — "now until 4 hours ahead" timeline view, toggleable to see everything.
  // Only flights that already have a session can appear — flight sync creates
  // them lookaheadHours ahead (sync_config.json), so keep that ≥ this window.
  const [timelineOnly, setTimelineOnly] = useState(true);

  const loadSessions = useCallback(async () => {
    try {
      const res = await gateSessionsAPI.getActive(timelineOnly ? { window_hours: TIMELINE_HOURS } : undefined);
      setSessions(res.data.data || []);
    } catch {
      toast.error('Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  }, [timelineOnly]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const res = await flightSyncAPI.getStatus();
      setSyncStatus(res.data.data);
    } catch {
      // Non-critical — ignore
    }
  }, []);

  const loadPendingCount = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getPending();
      setPendingCount((res.data.data || []).length);
    } catch {
      // Non-critical
    }
  }, []);

  // Real-time updates
  useSupervisorSocket({
    onEvent: (evt, data) => {
      if (['session:created', 'session:status'].includes(evt)) {
        loadSessions();
      }
      if (['announcement:submitted', 'announcement:status_change'].includes(evt)) {
        loadPendingCount();
      }
      if (evt === 'gate:online') {
        setGateStatuses(s => ({ ...s, [data.gateId]: { online: true, lastSeen: data.timestamp } }));
      }
      if (evt === 'gate:offline') {
        setGateStatuses(s => ({ ...s, [data.gateId]: { online: false, lastSeen: data.timestamp } }));
      }
    },
  });

  useEffect(() => {
    loadSessions();
    loadSyncStatus();
    loadPendingCount();
    // Refresh sessions every 30 seconds as fallback
    const sessionInterval = setInterval(loadSessions, 30000);
    // Refresh sync status every 60 seconds
    const syncInterval = setInterval(loadSyncStatus, 60000);
    // Refresh pending count every 30 seconds
    const pendingInterval = setInterval(loadPendingCount, 30000);
    return () => {
      clearInterval(sessionInterval);
      clearInterval(syncInterval);
      clearInterval(pendingInterval);
    };
  }, [loadSessions, loadSyncStatus, loadPendingCount]);

  async function handlePauseResume(session, e) {
    e.stopPropagation();
    const newStatus = session.status === 'paused' ? 'active' : 'paused';
    if (newStatus === 'paused' &&
        !window.confirm('Pause all announcements for this flight? This should only be used during an operational emergency.')) {
      return;
    }
    try {
      await gateSessionsAPI.updateStatus(session.id, newStatus);
      setSessions(s => s.map(sess =>
        sess.id === session.id ? { ...sess, status: newStatus } : sess
      ));
      toast.success(newStatus === 'paused' ? 'Gate paused' : 'Gate resumed');
    } catch {
      toast.error('Failed to update gate');
    }
  }

  async function handleSyncNow() {
    setSyncing(true);
    try {
      const res = await flightSyncAPI.runSync();
      const result = res.data.data;
      await loadSyncStatus();
      await loadSessions();
      toast.success(
        `Sync done — Created: ${result.created.length} | Gate changed: ${result.gateChanged.length} | Errors: ${result.errors.length}`
      );
    } catch (err) {
      if (!err._toasted) toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  function formatLastSync(iso) {
    if (!iso) return 'Never';
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  async function handleCancel(session, e) {
    e.stopPropagation();
    if (!confirm(`Cancel all announcements for flight ${session.flight_number}?`)) return;
    try {
      await gateSessionsAPI.updateStatus(session.id, 'cancelled');
      setSessions(s => s.filter(sess => sess.id !== session.id));
      toast.success('Session cancelled');
    } catch {
      toast.error('Failed to cancel session');
    }
  }

  const gateChangedEntries = syncStatus?.lastSyncResult?.gateChanged || [];
  const counts = useMemo(() => {
    const c = { all: sessions.length, pending: 0, acknowledged: 0, active: 0, paused: 0 };
    sessions.forEach(s => { c[themeKey(s)] += 1; });
    return c;
  }, [sessions]);

  const filteredSessions = useMemo(() => {
    let list = sessions;
    if (filterStatus !== 'all') list = list.filter(s => themeKey(s) === filterStatus);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(s =>
        s.gate?.gate_code?.toLowerCase().includes(q) ||
        s.flight_number?.toLowerCase().includes(q) ||
        s.destination?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [sessions, filterStatus, search]);

  // Start from the top whenever the view changes; a background refresh keeps
  // how far the user has scrolled.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const topRef = useRef(null);
  const firstViewRef = useRef(true);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    // Jump back to the top on a view change (not on first load), otherwise the
    // load-more sentinel is already on screen and pulls in several pages at once.
    if (firstViewRef.current) { firstViewRef.current = false; return; }
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [filterStatus, search, timelineOnly]);
  const visibleSessions = filteredSessions.slice(0, visibleCount);
  const hasMore = visibleCount < filteredSessions.length;

  // Load the next page when the sentinel below the grid scrolls into view
  // (300px early, so new cards are ready before the user reaches the end).
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const observer = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) setVisibleCount(c => c + PAGE_SIZE); },
      { rootMargin: '300px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, visibleCount]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div ref={topRef} className="min-h-full bg-slate-50">

      {/* ── Page header ─────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900">Live Gates</h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {sessions.length} flight{sessions.length === 1 ? '' : 's'} · {counts.active} live · synced {formatLastSync(syncStatus?.lastSyncAt)}
                {syncStatus?.isSyncing && <span className="ml-2 text-blue-500">· syncing…</span>}
              </p>
            </div>
          </div>

          <Button variant="primary" icon={RefreshCw} loading={syncing} onClick={handleSyncNow} className="flex-shrink-0">
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-5 max-w-7xl mx-auto space-y-5">

        {/* ── Stat row — one tile per card state (click to filter), then approvals/sync ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <StatCard label="Awaiting" value={counts.pending} color="slate" icon={<Hourglass size={16} />}
            sub="not acknowledged" active={filterStatus === 'pending'} onClick={() => setFilterStatus(f => f === 'pending' ? 'all' : 'pending')} />
          <StatCard label="Acknowledged" value={counts.acknowledged} color="orange" icon={<UserCheck size={16} />}
            sub="ready to initiate" active={filterStatus === 'acknowledged'} onClick={() => setFilterStatus(f => f === 'acknowledged' ? 'all' : 'acknowledged')} />
          <StatCard label="Live" value={counts.active} color="emerald" icon={<Radio size={16} />}
            sub="programme running" active={filterStatus === 'active'} onClick={() => setFilterStatus(f => f === 'active' ? 'all' : 'active')} />
          <StatCard label="Paused" value={counts.paused} color={counts.paused > 0 ? 'rose' : 'slate'} icon={<Pause size={16} />}
            sub={counts.paused > 0 ? 'need attention' : 'none paused'} active={filterStatus === 'paused'} onClick={() => setFilterStatus(f => f === 'paused' ? 'all' : 'paused')} />
          <StatCard label="Approvals" value={pendingCount} color={pendingCount > 0 ? 'blue' : 'slate'} icon={<Globe size={16} />}
            sub={pendingCount > 0 ? 'awaiting review' : 'up to date'} onClick={() => navigate('/supervisor/announcements')} />
          <StatCard label="Last Sync" value={formatLastSync(syncStatus?.lastSyncAt)} color="blue" icon={<RefreshCw size={16} />}
            sub="FlightMS" isText />
        </div>

        {/* ── Gate-changed alert ─────────────────────────────── */}
        {gateChangedEntries.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 sm:px-5 py-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800 mb-2">
                {gateChangedEntries.length} gate change{gateChangedEntries.length > 1 ? 's' : ''} detected in last sync
              </p>
              <div className="flex flex-wrap gap-2">
                {gateChangedEntries.map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-amber-200 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-lg">
                    <span className="font-bold">{item.flightNumber}</span>
                    <span className="text-amber-300">·</span>
                    {item.oldGate} <ChevronRight size={10} className="text-amber-400" /> <span className="font-bold">{item.newGate}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Search (left) + state filters + time window ───────── */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 basis-full sm:basis-auto min-w-0 sm:min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search gate, flight, destination…"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          {/* Scrolls sideways on phones rather than widening the page */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 max-w-full overflow-x-auto">
            {FILTERS.map(({ key, label }) => ({ key, label, count: counts[key] })).map(({ key, label, count }) => (
              <button key={key} type="button"
                onClick={() => setFilterStatus(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  filterStatus === key
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {label}
                {count > 0 && (
                  <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${
                    filterStatus === key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* CR 9.2 — 3-hour timeline toggle */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 flex-shrink-0">
            {[
              { key: true,  label: `Next ${TIMELINE_HOURS}h` },
              { key: false, label: 'All Time' },
            ].map(({ key, label }) => (
              <button key={String(key)} type="button"
                onClick={() => setTimelineOnly(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  timelineOnly === key
                    ? 'bg-blue-700 text-white shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Gate cards ─────────────────────────────────────── */}
        {sessions.length === 0 ? (
          <EmptyState icon={Radio} title="No gate sessions" subtitle={timelineOnly ? `No flights in the next ${TIMELINE_HOURS} hours — switch to All Time to see later ones` : 'Sessions appear here when flights are synced or operators start them'} />
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Search size={28} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No gates match</p>
            <button onClick={() => { setSearch(''); setFilterStatus('all'); }}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleSessions.map(session => (
              <GateCard
                key={session.id}
                session={session}
                gateStatus={gateStatuses[session.gate_id]}
                onPauseResume={handlePauseResume}
                onCancel={handleCancel}
                onClick={() => navigate(queuePathFor(user?.role, session.gate_id, session.id))}
              />
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel + footer */}
        {filteredSessions.length > 0 && (
          <div ref={sentinelRef} data-load-more className="flex items-center justify-center gap-2 py-4 text-xs text-slate-400">
            {hasMore ? (
              <>
                <div className="w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
                Loading more flights…
              </>
            ) : (
              <span>
                Showing all {filteredSessions.length} flight{filteredSessions.length === 1 ? '' : 's'}
                {timelineOnly ? ` in the next ${TIMELINE_HOURS} hours` : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, color, icon, sub, onClick, active, isText }) {
  const colors = {
    emerald: { icon: 'bg-emerald-100 text-emerald-600', val: 'text-emerald-700', ring: 'ring-emerald-400' },
    orange:  { icon: 'bg-orange-100 text-orange-600',   val: 'text-orange-600',  ring: 'ring-orange-400' },
    rose:    { icon: 'bg-rose-100 text-rose-600',       val: 'text-rose-600',    ring: 'ring-rose-400' },
    blue:    { icon: 'bg-blue-100 text-blue-600',       val: 'text-blue-700',    ring: 'ring-blue-400' },
    slate:   { icon: 'bg-slate-100 text-slate-500',     val: 'text-slate-700',   ring: 'ring-slate-400' },
  };
  const c = colors[color] || colors.slate;

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={`min-w-0 bg-white rounded-2xl border border-slate-200 px-3 sm:px-4 py-3.5 flex items-center gap-2.5 sm:gap-3 transition-all ${
        onClick ? 'cursor-pointer hover:border-slate-300 hover:shadow-md' : ''
      } ${active ? `ring-2 ${c.ring} shadow-md` : ''}`}
    >
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.icon}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 truncate">{label}</p>
        <p className={`font-black leading-tight ${isText ? 'text-base text-slate-800' : `text-2xl ${c.val}`}`}>
          {value}
        </p>
        <p className="text-[11px] text-slate-400 truncate">{sub}</p>
      </div>
    </div>
  );
}

// ── Gate Card ─────────────────────────────────────────────────────────────────

function GateCard({ session, gateStatus, onPauseResume, onCancel, onClick }) {
  const key = themeKey(session);
  const t = THEMES[key];
  const StateIcon = t.icon;
  const canPauseResume = ['active', 'paused'].includes(session.status);
  const isOnline  = gateStatus?.online === true;
  const isOffline = gateStatus?.online === false;
  const anns = session.announcements || [];
  const queuedCount = anns.filter(a => ['pending', 'generating', 'ready'].includes(a.status)).length;
  const { played: doneCount, total: progressTotal } = sessionProgress(anns);
  const playingAnn = anns.find(a => a.status === 'playing');
  const interruptedAnn = anns.find(a => a.status === 'interrupted');
  const depTime = new Date(session.departure_time);
  const countdown = departureCountdown(session.departure_time);

  return (
    <div
      onClick={onClick}
      role="button"
      data-gate-card
      className={`group rounded-2xl border-2 ${t.card} shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer overflow-hidden flex flex-col`}
    >
      {/* ── Colored band: gate + state ── */}
      <div className={`px-5 py-3.5 ${t.band} flex items-center justify-between gap-3`}>
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-3xl font-black tracking-tight leading-none">{session.gate?.gate_code}</span>
          {session.gate?.terminal && (
            <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${t.pill}`}>
              {String(session.gate.terminal).startsWith('T') ? session.gate.terminal : `T${session.gate.terminal}`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isOnline && <Wifi size={14} className="opacity-80" title="Gate device online" />}
          {isOffline && <WifiOff size={14} className="opacity-80" title="Gate device offline" />}
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${t.pill}`}>
            {t.live
              ? <span className="relative flex w-2 h-2"><span className="absolute inline-flex w-full h-full rounded-full bg-white opacity-75 animate-ping" /><span className="relative inline-flex w-2 h-2 rounded-full bg-white" /></span>
              : <StateIcon size={12} />}
            {t.label}
          </span>
        </div>
      </div>

      {/* ── Flight ── */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <PlaneTakeoff size={14} className="text-slate-400 flex-shrink-0" />
              <span className="text-base font-black text-slate-900">{session.flight_number}</span>
              <ChevronRight size={14} className="text-slate-300" />
              <span className="text-sm font-semibold text-slate-700 truncate">{session.destination || '—'}</span>
            </div>
            {session.airline && <p className="text-xs text-slate-400 mt-0.5 ml-5 truncate">{session.airline}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm font-bold text-slate-800 tabular-nums">
              {depTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className={`text-[11px] font-semibold ${countdown.urgent ? 'text-red-600' : 'text-slate-400'}`}>{countdown.text}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-white/80 ring-1 ring-slate-200 px-2 py-0.5 rounded-md">
            <Globe size={11} className="text-slate-400" />
            {languagePairLabel(session.secondary_language, { short: true, fallback: null })}
          </span>
          {/* session.operator is created_by (the system account for auto-synced
              sessions) — the person who actually acknowledged is what matters here */}
          {session.acknowledgedByUser && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-white/80 ring-1 ring-slate-200 px-2 py-0.5 rounded-md">
              <User size={11} className="text-slate-400" />
              {session.acknowledgedByUser.name}
            </span>
          )}
        </div>
      </div>

      {/* ── What's happening now ── */}
      <div className="mx-5 mb-4 rounded-xl bg-white/80 ring-1 ring-slate-200/70 px-3.5 py-2.5 min-h-[44px] flex items-center">
        {interruptedAnn ? (
          <div className="flex items-center gap-2 text-xs">
            <Pause size={14} className="text-rose-500 flex-shrink-0" />
            <span className="font-bold text-rose-700">Interrupted by Emergency Pause</span>
            <span className="text-rose-600 capitalize truncate">{interruptedAnn.type_label || interruptedAnn.announcement_type?.replace(/_/g, ' ')}</span>
          </div>
        ) : playingAnn ? (
          <div className="flex items-center gap-2.5 text-xs min-w-0">
            <div className="flex gap-0.5 items-end h-4 flex-shrink-0">
              {[2, 4, 3, 5, 2].map((h, i) => (
                <div key={i} className="w-0.5 bg-emerald-500 rounded-full animate-bounce"
                  style={{ height: `${h * 3}px`, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
            <span className="font-bold text-emerald-700">Now playing</span>
            <span className="text-emerald-700/80 capitalize truncate">{playingAnn.type_label || playingAnn.announcement_type?.replace(/_/g, ' ')}</span>
          </div>
        ) : key === 'pending' ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Hourglass size={13} className="flex-shrink-0" /> Waiting for the gate operator to acknowledge
          </div>
        ) : key === 'acknowledged' ? (
          <div className="flex items-center gap-2 text-xs text-orange-700 font-medium">
            <Timer size={13} className="flex-shrink-0" /> Operator at the gate — ready to initiate
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 w-full text-xs text-slate-500">
            <span className="flex items-center gap-2">
              <Clock size={13} className="flex-shrink-0" />
              {queuedCount > 0
                ? <span><span className="font-bold text-slate-800">{queuedCount}</span> queued</span>
                : <span>Nothing queued</span>}
            </span>
            {progressTotal > 0 && <span className="font-semibold text-slate-600 tabular-nums">{doneCount}/{progressTotal} played</span>}
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="px-4 pb-4 mt-auto flex gap-2" onClick={e => e.stopPropagation()}>
        {canPauseResume && (
          <Button
            variant={session.status === 'paused' ? 'success' : 'warning'}
            icon={session.status === 'paused' ? Play : Pause}
            onClick={e => onPauseResume(session, e)}
            title={session.status === 'paused' ? 'Resume' : 'Emergency Pause'}
            className="flex-1"
          >
            {session.status === 'paused' ? 'Resume' : 'Emergency Pause'}
          </Button>
        )}
        <button
          onClick={e => onCancel(session, e)}
          className={`${canPauseResume ? '' : 'flex-1'} flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-slate-500 bg-white hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition-colors`}
          title="Cancel session"
        >
          <XCircle size={13} /> Cancel
        </button>
        <button
          onClick={() => window.open(`/gate-display/${session.gate_id}`, '_blank', 'noopener,noreferrer')}
          className="flex items-center justify-center px-3 py-2 rounded-xl text-xs font-bold text-slate-500 bg-white hover:text-slate-900 hover:bg-slate-100 border border-slate-200 transition-colors"
          title="Open gate display"
        >
          <Monitor size={13} />
        </button>
      </div>
    </div>
  );
}
